/**
 * Bazaarvoice contrast patch.
 *
 * The widget paints its accent in #17b6d9 / rgb(23, 182, 217), which is 2.4:1 against
 * white - below the 4.5:1 that WCAG AA asks of text and UI text. assets/custom.css
 * overrides every BV class name we know of, but that only reaches what we can name:
 * Bazaarvoice ships its own stylesheet at runtime, renames classes between releases,
 * and can render into a shadow root, which no stylesheet of ours can cross.
 *
 * This reads the colour the browser actually resolved for each element and, only where
 * that colour is the failing blue, pins the replacement inline with priority. Nothing
 * in the cascade outranks that. It touches no element whose colour is already
 * something else, so ordinary review text is left alone.
 *
 * Replacement #0c7a99 is the same hue at 4.94:1 on white.
 *
 * The widget has been seen to come back unpatched on some loads, so this version makes
 * no assumptions about WHEN or WHERE it renders:
 *   - scheduling uses setTimeout, not requestAnimationFrame, which is throttled to
 *     nothing in background tabs and in some headless crawlers;
 *   - the observer watches attributes as well as childList, so a re-skin that only
 *     rewrites inline styles on existing nodes is caught;
 *   - a bounded poll keeps checking after load, in case a mutation is ever missed;
 *   - periodic full-document sweeps catch a widget that renders outside the container
 *     selectors we know, e.g. after a vendor class rename.
 *
 * window.__bvContrast reports what it found - see the bottom of this file.
 */
(function () {
  'use strict';

  var FAIL = /^rgba?\(\s*23\s*,\s*182\s*,\s*217\s*[,)]/;
  var PASS = '#0c7a99';
  var PASS_HEX = /^#17b6d9$/i;

  // Colour-bearing properties the widget is known to use, plus the ones it could.
  var PROPS = [
    'color',
    'fill',
    'stroke',
    'background-color',
    'border-top-color',
    'border-right-color',
    'border-bottom-color',
    'border-left-color',
    'outline-color',
    'text-decoration-color'
  ];

  // Containers the theme hands to Bazaarvoice, plus the ones the widget creates.
  var ROOTS = [
    '[data-bv-show]',
    '[data-bv-product-id]',
    '.bv_main_container',
    '.bv_ratings_summary',
    '[id^="BVRR"]',
    '[id^="BVQA"]',
    '[id^="bv-"]',
    '[class^="bv_"]',
    '[class*=" bv_"]',
    '[class^="bv-"]',
    '[class*=" bv-"]'
  ].join(',');

  // Full-document sweeps are the fallback for a widget we cannot name. They cost one
  // style read per element, so they run on a schedule rather than on every pass.
  var SWEEP_AT = [0, 1000, 2500, 5000, 9000, 15000, 25000];
  var POLL_EVERY = 1000;
  var POLL_UNTIL = 60000;

  var observed = typeof WeakSet === 'function' ? new WeakSet() : null;
  var scheduled = false;
  var patching = false;
  var started = Date.now();

  var stats = { runs: 0, sweeps: 0, patched: 0, roots: 0, lastPatchAt: null, frames: 0 };

  function patchElement(el) {
    if (!el || el.nodeType !== 1) return;

    var computed;
    try {
      computed = window.getComputedStyle(el);
    } catch (e) {
      return;
    }
    if (!computed) return;

    var hit = false;

    for (var i = 0; i < PROPS.length; i++) {
      var prop = PROPS[i];
      var value = computed.getPropertyValue(prop);
      if (value && FAIL.test(value.trim())) {
        el.style.setProperty(prop, PASS, 'important');
        hit = true;
      }
    }

    // SVG presentation attributes are not reached by style.setProperty.
    if (el.getAttribute) {
      var fill = el.getAttribute('fill');
      if (fill && PASS_HEX.test(fill.trim())) { el.setAttribute('fill', PASS); hit = true; }
      var stroke = el.getAttribute('stroke');
      if (stroke && PASS_HEX.test(stroke.trim())) { el.setAttribute('stroke', PASS); hit = true; }
      var stop = el.getAttribute('stop-color');
      if (stop && PASS_HEX.test(stop.trim())) { el.setAttribute('stop-color', PASS); hit = true; }
    }

    if (hit) {
      stats.patched++;
      stats.lastPatchAt = Date.now() - started;
    }
  }

  function walk(root) {
    if (!root || !root.querySelectorAll) return;

    var nodes = root.querySelectorAll('*');
    for (var i = 0; i < nodes.length; i++) {
      patchElement(nodes[i]);
      // An open shadow root keeps its own cascade, so our stylesheet never lands there.
      if (nodes[i].shadowRoot) {
        observe(nodes[i].shadowRoot, true);
        walk(nodes[i].shadowRoot);
      }
    }
  }

  function patchScoped() {
    var roots = document.querySelectorAll(ROOTS);
    stats.roots = roots.length;

    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      // The selector list matches nested bv_ elements too. Walking only the outermost
      // match keeps one subtree from being traversed once per descendant it contains.
      var parent = root.parentElement;
      if (parent && parent.closest && parent.closest(ROOTS)) continue;

      observe(root, true);
      patchElement(root);
      walk(root);
      if (root.shadowRoot) {
        observe(root.shadowRoot, true);
        walk(root.shadowRoot);
      }
    }

    return roots.length;
  }

  function sweep() {
    stats.sweeps++;
    if (document.body) walk(document.body);
  }

  function run(full) {
    if (patching) return;
    patching = true;
    scheduled = false;
    stats.runs++;

    try {
      var found = patchScoped();
      // No recognisable container means the vendor markup is not where we expect it,
      // so fall back to looking at the whole page.
      if (full || !found) sweep();
    } finally {
      patching = false;
    }
  }

  // setTimeout, not requestAnimationFrame: rAF does not fire in a background tab or in
  // a headless crawler that never paints, which would leave the widget unpatched.
  // The small delay coalesces bursts of mutations into a single pass.
  function schedule() {
    if (scheduled || patching) return;
    scheduled = true;
    window.setTimeout(function () { run(false); }, 100);
  }

  /**
   * withAttributes is set only for Bazaarvoice subtrees. Watching style/class changes
   * across the whole page would fire on every slider and sticky-header animation the
   * theme runs; inside the widget it is what catches a re-skin that rewrites inline
   * styles without replacing any nodes.
   *
   * This cannot feed itself: a second pass finds the colour already replaced, so it
   * writes nothing and generates no further mutations.
   */
  function observe(target, withAttributes) {
    if (!target || typeof MutationObserver !== 'function') return;
    if (observed) {
      if (observed.has(target)) return;
      observed.add(target);
    }

    var options = { childList: true, subtree: true };
    if (withAttributes) {
      options.attributes = true;
      options.attributeFilter = ['style', 'class', 'fill', 'stroke', 'stop-color'];
    }

    new MutationObserver(function () {
      if (patching) return;
      schedule();
    }).observe(target, options);
  }

  function start() {
    observe(document.body, false);

    for (var i = 0; i < SWEEP_AT.length; i++) {
      (function (delay) {
        window.setTimeout(function () { run(true); }, delay);
      })(SWEEP_AT[i]);
    }

    // Bounded poll: the safety net if a mutation is ever missed entirely.
    var poll = window.setInterval(function () {
      if (Date.now() - started > POLL_UNTIL) {
        window.clearInterval(poll);
        return;
      }
      run(false);
    }, POLL_EVERY);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('load', function () { run(true); });
  window.addEventListener('pageshow', function () { run(true); });

  /**
   * Diagnostic. On a page that still shows the failing blue, run this in the console:
   *
   *   __bvContrast.report()
   *
   * patched > 0  the patch is working; anything still blue was painted after the last
   *              pass, so send me what report() prints.
   * roots  === 0 the widget is not in any container we recognise - vendor rename.
   * frames  > 0  the widget is inside an iframe. If that iframe is on a bazaarvoice.com
   *              origin, nothing in this theme can reach it and it needs a BV ticket.
   */
  window.__bvContrast = {
    stats: stats,
    run: function () { run(true); return stats; },
    report: function () {
      var frames = document.querySelectorAll('iframe');
      var bvFrames = [];
      for (var i = 0; i < frames.length; i++) {
        var src = frames[i].src || frames[i].getAttribute('data-src') || '';
        if (/bazaarvoice|bvapi|\bbv\b/i.test(src + ' ' + (frames[i].getAttribute('class') || '') + ' ' + (frames[i].id || ''))) {
          bvFrames.push(src || '(no src)');
        }
      }
      stats.frames = bvFrames.length;

      var stillFailing = [];
      var all = document.querySelectorAll('*');
      for (var j = 0; j < all.length; j++) {
        try {
          var c = window.getComputedStyle(all[j]).getPropertyValue('color');
          if (c && FAIL.test(c.trim())) {
            stillFailing.push({
              tag: all[j].tagName.toLowerCase(),
              cls: all[j].getAttribute('class') || '',
              text: (all[j].textContent || '').trim().slice(0, 40)
            });
          }
        } catch (e) { /* ignore */ }
      }

      return {
        runs: stats.runs,
        sweeps: stats.sweeps,
        patched: stats.patched,
        rootsFound: stats.roots,
        lastPatchAtMs: stats.lastPatchAt,
        bvIframes: bvFrames,
        stillFailing: stillFailing
      };
    }
  };
})();
